import React, { useEffect, useReducer, useState } from "react";
import { makeStyles } from 'tss-react/mui';
import toastError from "../../errors/toastError";
import Popover from "@mui/material/Popover";
import CampaignIcon from "@mui/icons-material/Campaign";
import PropTypes from 'prop-types';
import {
  Avatar,
  Badge,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Dialog,
  Paper,
  Typography,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  DialogContentText,
} from "@mui/material";
import api from "../../services/api";
import { isArray } from "lodash";
import moment from "moment";
import { socketConnection } from "../../services/socket";

const useStyles = makeStyles()((theme) => ({
  popoverPaper: {
    width: 370,
    maxHeight: 440,
    display: "flex",
    flexDirection: "column",
    background: theme.palette.background.paper,
  },
  header: {
    padding: "12px 16px",
    borderBottom: `1px solid ${theme.palette.divider}`,
    fontWeight: 700,
    fontSize: 15,
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexShrink: 0,
  },
  list: {
    overflowY: "auto",
    padding: theme.spacing(1),
    ...theme.scrollbarStyles,
  },
  item: {
    display: "flex",
    gap: 10,
    padding: 10,
    borderRadius: 10,
    cursor: "pointer",
    marginBottom: 6,
    background: theme.palette.mode === "light" ? "#f6f7f9" : "rgba(255,255,255,0.03)",
    borderLeft: "4px solid transparent",
    transition: "background .15s",
    "&:hover": { background: theme.palette.action.hover },
  },
  thumb: {
    width: 44,
    height: 44,
    borderRadius: 8,
    flexShrink: 0,
  },
  itemBody: { minWidth: 0, flex: 1 },
  itemTitleRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 6,
  },
  itemTitle: {
    fontWeight: 600,
    fontSize: 14,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  itemDate: {
    fontSize: 11,
    color: theme.palette.text.secondary,
    flexShrink: 0,
  },
  itemText: {
    fontSize: 12.5,
    color: theme.palette.text.secondary,
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
    marginTop: 2,
  },
  priorityChip: {
    display: "inline-block",
    fontSize: 10,
    fontWeight: 700,
    padding: "1px 6px",
    borderRadius: 6,
    textTransform: "uppercase",
    color: "#fff",
    marginTop: 4,
  },
  empty: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    padding: 40,
    color: theme.palette.text.secondary,
  },
  dialogMedia: {
    margin: "0 auto 20px",
    width: "100%",
    height: 300,
    borderRadius: 8,
    border: `1px solid ${theme.palette.divider}`,
    backgroundRepeat: "no-repeat",
    backgroundSize: "contain",
    backgroundPosition: "center",
  },
  dialogHeader: {
    padding: theme.spacing(2.5),
    display: "flex",
    alignItems: "center",
    gap: 16,
    color: "#fff",
  },
  dialogHeaderIcon: {
    fontSize: 44,
    flexShrink: 0,
  },
  dialogKicker: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 1.5,
    textTransform: "uppercase",
    opacity: 0.9,
  },
  dialogTitle2: {
    fontSize: 20,
    fontWeight: 700,
    lineHeight: 1.2,
    wordBreak: "break-word",
  },
  dialogMeta: {
    display: "flex",
    gap: 8,
    alignItems: "center",
    marginTop: 4,
    fontSize: 12.5,
    opacity: 0.92,
  },
}));

function AnnouncementDialog({ announcement, open, handleClose, classes }) {
  const getMediaPath = (filename) => {
    return `${process.env.REACT_APP_BACKEND_URL}/public/${filename}`;
  };
  const meta = priorityMeta(announcement.priority);
  return (
    <Dialog
      open={open}
      onClose={() => handleClose()}
      maxWidth="sm"
      fullWidth
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <div className={classes.dialogHeader} style={{ background: meta.color }}>
        <CampaignIcon className={classes.dialogHeaderIcon} />
        <div style={{ minWidth: 0 }}>
          <div className={classes.dialogKicker}>Informativo</div>
          <div className={classes.dialogTitle2}>{announcement.title}</div>
          <div className={classes.dialogMeta}>
            <span>Prioridad {meta.label}</span>
            {announcement.createdAt && (
              <span>· {moment(announcement.createdAt).format("DD/MM/YYYY")}</span>
            )}
          </div>
        </div>
      </div>
      <DialogContent dividers>
        {announcement.mediaPath && (
          <div
            className={classes.dialogMedia}
            style={{
              backgroundImage: `url(${getMediaPath(announcement.mediaPath)})`,
            }}
          ></div>
        )}
        <DialogContentText id="alert-dialog-description" style={{ whiteSpace: "pre-wrap" }}>
          {announcement.text}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => handleClose()} color="primary" variant="contained" autoFocus>
          Entendido
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const reducer = (state, action) => {
  if (action.type === "LOAD_ANNOUNCEMENTS") {
    const announcements = action.payload;
    const newAnnouncements = [];

    if (isArray(announcements)) {
      announcements.forEach((announcement) => {
        const announcementIndex = state.findIndex(
          (u) => u.id === announcement.id
        );
        if (announcementIndex !== -1) {
          state[announcementIndex] = announcement;
        } else {
          newAnnouncements.push(announcement);
        }
      });
    }

    return [...state, ...newAnnouncements];
  }

  if (action.type === "UPDATE_ANNOUNCEMENTS") {
    const announcement = action.payload;
    const announcementIndex = state.findIndex((u) => u.id === announcement.id);

    if (announcementIndex !== -1) {
      state[announcementIndex] = announcement;
      return [...state];
    } else {
      return [announcement, ...state];
    }
  }

  if (action.type === "DELETE_ANNOUNCEMENT") {
    const announcementId = action.payload;

    const announcementIndex = state.findIndex((u) => u.id === announcementId);
    if (announcementIndex !== -1) {
      state.splice(announcementIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const priorityMeta = (priority) => {
  if (priority === 1) return { color: "#e53935", label: "Alta" };
  if (priority === 2) return { color: "#fb8c00", label: "Media" };
  return { color: "#9e9e9e", label: "Baja" };
};

export default function AnnouncementsPopover() {
  const { classes } = useStyles();

  const [loading, setLoading] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [searchParam] = useState("");
  const [announcements, dispatch] = useReducer(reducer, []);
  const [invisible, setInvisible] = useState(false);
  const [announcement, setAnnouncement] = useState({});
  const [showAnnouncementDialog, setShowAnnouncementDialog] = useState(false);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      fetchAnnouncements();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParam, pageNumber]);

  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    const socket = socketConnection({ companyId });

    socket.on(`company-announcement`, (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_ANNOUNCEMENTS", payload: data.record });
        setInvisible(false);
      }
      if (data.action === "delete") {
        dispatch({ type: "DELETE_ANNOUNCEMENT", payload: +data.id });
      }
    });
    return () => {
      socket.disconnect();
    };
  }, []);

  const fetchAnnouncements = async () => {
    try {
      const { data } = await api.get("/announcements/", {
        params: { searchParam, pageNumber },
      });
      dispatch({ type: "LOAD_ANNOUNCEMENTS", payload: data.records });
      setHasMore(data.hasMore);
      setLoading(false);
    } catch (err) {
      toastError(err);
    }
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
    setInvisible(true);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const getMediaPath = (filename) => {
    return `${process.env.REACT_APP_BACKEND_URL}/public/${filename}`;
  };

  const handleShowAnnouncementDialog = (record) => {
    setAnnouncement(record);
    setShowAnnouncementDialog(true);
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? "simple-popover" : undefined;

  return (
    <div>
      <AnnouncementDialog
        announcement={announcement}
        open={showAnnouncementDialog}
        handleClose={() => setShowAnnouncementDialog(false)}
        classes={classes}
      />
      <IconButton
        variant="contained"
        aria-describedby={id}
        onClick={handleClick}
        style={{color : '#FFF'}}
        size="large">
        <Badge
          color="secondary"
          variant="dot"
          invisible={invisible || announcements.length < 1}
        >
          <CampaignIcon />
        </Badge>
      </IconButton>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        <Paper elevation={0} className={classes.popoverPaper}>
          <div className={classes.header}>
            <CampaignIcon fontSize="small" color="primary" />
            Informativos
          </div>
          <div className={classes.list} onScroll={handleScroll}>
            {isArray(announcements) &&
              announcements.map((item, key) => {
                const meta = priorityMeta(item.priority);
                return (
                  <div
                    key={key}
                    className={classes.item}
                    style={{ borderLeftColor: meta.color }}
                    onClick={() => handleShowAnnouncementDialog(item)}
                  >
                    {item.mediaPath && (
                      <Avatar
                        variant="rounded"
                        className={classes.thumb}
                        alt={item.mediaName}
                        src={getMediaPath(item.mediaPath)}
                      />
                    )}
                    <div className={classes.itemBody}>
                      <div className={classes.itemTitleRow}>
                        <span className={classes.itemTitle}>{item.title || "(Sin título)"}</span>
                        <span className={classes.itemDate}>
                          {moment(item.createdAt).format("DD/MM/YYYY")}
                        </span>
                      </div>
                      {item.text && <div className={classes.itemText}>{item.text}</div>}
                      <span
                        className={classes.priorityChip}
                        style={{ background: meta.color }}
                      >
                        {meta.label}
                      </span>
                    </div>
                  </div>
                );
              })}
            {isArray(announcements) && announcements.length === 0 && (
              <div className={classes.empty}>
                <CampaignIcon style={{ fontSize: 40, opacity: 0.4 }} />
                <Typography variant="body2">No hay informativos</Typography>
              </div>
            )}
          </div>
        </Paper>
      </Popover>
    </div>
  );
}
